import type { ApiErrorCode, ApiErrorDto, QuotaExceededDetailsDto } from '@shared/api-dto';

/**
 * A failed API call, carrying the server's `error.code`.
 *
 * The UI must branch on `code`, NEVER on HTTP status. Three different 403s mean three completely
 * different screens:
 *   NO_ENTITLEMENT → the org has no subscription  → show plans
 *   NO_SEAT        → subscribed, but this user has no seat → "ask your admin"
 *   FORBIDDEN      → seated, but the role lacks the permission → hide the action
 * Collapsing them by status would send a seatless member to a pricing page for a plan their org has
 * already paid for.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(status: number, body: ApiErrorDto['error']) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
    this.requestId = body.requestId;
  }

  /** Quota details live in the BODY, readable cross-origin even where headers are not. */
  get quota(): QuotaExceededDetailsDto | null {
    return this.code === 'QUOTA_EXCEEDED' ? (this.details as QuotaExceededDetailsDto) : null;
  }
}

/** What the UI should DO about a failure — the mapping, in one place. */
export type Outcome =
  | { kind: 'unauthenticated' }
  | { kind: 'needs-plan' } // NO_ENTITLEMENT → plan selection
  | { kind: 'needs-seat' } // NO_SEAT → ask an admin
  | { kind: 'not-permitted' } // FORBIDDEN → hide/disable the action
  | { kind: 'quota-exceeded'; quota: QuotaExceededDetailsDto | null } // → upsell
  | { kind: 'rate-limited'; retryAfterSeconds: number | null }
  | { kind: 'invalid'; details: unknown }
  | { kind: 'conflict'; message: string }
  /** The org already spent its free trial for this product. Carries NO message: the server's copy is
   *  English, and this is a Hebrew product — the client owns the sentence, which is the whole reason
   *  this is its own kind rather than another `conflict`. */
  | { kind: 'trial-used' }
  | { kind: 'not-configured' } // 501 → this deployment can't do that yet; NOT the user's fault
  | { kind: 'error'; message: string };

export function toOutcome(err: unknown): Outcome {
  if (!(err instanceof ApiError)) {
    return { kind: 'error', message: err instanceof Error ? err.message : 'Something went wrong' };
  }
  switch (err.code) {
    case 'UNAUTHORIZED':
      return { kind: 'unauthenticated' };
    case 'NO_ENTITLEMENT':
      return { kind: 'needs-plan' };
    case 'NO_SEAT':
      return { kind: 'needs-seat' };
    case 'FORBIDDEN':
      return { kind: 'not-permitted' };
    case 'QUOTA_EXCEEDED':
      return { kind: 'quota-exceeded', quota: err.quota };
    case 'RATE_LIMITED':
      return { kind: 'rate-limited', retryAfterSeconds: null };
    case 'VALIDATION':
      return { kind: 'invalid', details: err.details };
    case 'TRIAL_ALREADY_USED':
      // Split out of `conflict` deliberately. Folded in with the others, the only thing telling this
      // apart from "no seats available" was the server's English prose — which both got SHOWN to a
      // Hebrew user, and which AutoEnroll had to match on to decide a trial was spent.
      return { kind: 'trial-used' };
    case 'CONFLICT':
    case 'NO_SEATS_AVAILABLE':
      return { kind: 'conflict', message: err.message };
    case 'NOT_CONFIGURED':
      // Google SSO or billing isn't wired up in this environment. Retrying, re-entering details or
      // picking another plan will never help — only we can fix it, so say so plainly.
      return { kind: 'not-configured' };
    default:
      return { kind: 'error', message: err.message };
  }
}
